import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/http';
import { queryKeyRoots } from '@/api/queryKeys';
import { questionSetsQuery, type QuestionSet } from '@/api/questionSets';
import {
  attachQuestionSet,
  detachQuestionSet,
  roleQuestionSetsQuery,
  type Role,
} from '@/api/vacancies';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field } from '@/components/ui/Field';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  vacancy: Role;
  /** The session user's id — the sets list is narrowed per reader, so its cache key needs one. */
  viewerId: number;
  onClose: () => void;
}

/**
 * §6.8's **Question sets** — which sets this vacancy offers, and the two writes that change it
 * (Sprint 20, TASK-144).
 *
 * ⚠️ **This is where the user looked for it**: *"I cannot see how to add them to a vacancy for an
 * interview"* — their own words, 2026-08-12, translated (the Russian is quoted in ADR 0018).
 * The set surface itself is §6.9's; **attaching one to a vacancy lives on the vacancy**, because it is
 * a fact about the vacancy rather than about the set — which is also why the routes are
 * `/api/v1/roles/{id}/question-sets/` and why their permission is the **catalogue's** (`Admin`) rather
 * than the set's per-row authorship.
 *
 * ⚠️ **Attaching is an offer, not a rule, and the copy has to say so** (ADR 0018 decision 3, which is
 * ADR 0010's reasoning with the nouns changed). What it changes is which sets §6.5's scheduling dialog
 * shows **first**; the server refuses **no** set on the grounds that it is not attached to the vacancy,
 * and a set attached to nothing at all schedules exactly as well. A reader taught that detaching a set
 * stops anybody using it would have been taught the funnel rule this product already refused once — so
 * the dialog says what attaching does and the detach confirmation says what it does **not** do.
 *
 * ⚠️ **Admin only, hidden and never disabled** (§5): a `Recruiter` reads §6.8 and writes nothing on
 * it, so the menu item that opens this is not rendered for them at all (`VacancyMenu`,
 * `mayWriteVacancies`). The client gate is never the evidence — `write_access` refuses them itself.
 *
 * ⚠️ **A retired set stays attached and reads as retired.** Retiring is not detaching, and hiding it
 * here would make it invisible to whoever wants to restore it — while `_set_for` already refuses to
 * pick one as a vacancy's default, so it costs nothing to leave it on view.
 *
 * ⚠️ **Both writes invalidate the `roles` root and nothing is written into the cache by hand.**
 * `RoleOut.active_question_count` counts the distinct active questions of a vacancy's active sets, so
 * an attach or a detach moves the grid's figure — and the figure the reader sees afterwards is read
 * back off the refetch rather than computed here. The root reaches the grid, the single-vacancy entry
 * and this dialog's own list, which is why `queryKeys.roleQuestionSets` lives in that family.
 */
export function VacancySetsDialog({ vacancy, viewerId, onClose }: Props) {
  const { t } = useTranslation(['vacancies', 'common', 'components']);
  const queryClient = useQueryClient();

  const [chosen, setChosen] = useState('');

  const attached = useQuery(roleQuestionSetsQuery(vacancy.id));

  /**
   * ⚠️ **The tenant's sets, read from the same list §6.9's tab reads** — narrowed per caller by the
   * server (ADR 0018's reach predicate), which is why the key carries the viewer. Retired sets are
   * **not** asked for here, unlike on §6.9: a retired set cannot be scheduled with, so offering one to
   * attach would be offering a set nothing can use.
   */
  const catalogue = useQuery(questionSetsQuery(viewerId, { page_size: 100 }));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeyRoots.roles });
  };

  const attach = useMutation<QuestionSet[], ApiError, number>({
    mutationFn: (setId) => attachQuestionSet(vacancy.id, setId),
    onSuccess: () => {
      invalidate();
      setChosen('');
    },
  });

  const detach = useMutation<QuestionSet[], ApiError, number>({
    mutationFn: (setId) => detachQuestionSet(vacancy.id, setId),
    onSuccess: invalidate,
  });

  const rows = attached.data ?? [];
  const attachedIds = rows.map((row) => row.id);
  const offerable = (catalogue.data?.items ?? []).filter((set) => !attachedIds.includes(set.id));

  const failure = (() => {
    const error = attach.error ?? detach.error;
    if (!error) return null;
    if (error.isOffline) return t('components:state.offlineWrite');
    if (error.isThrottled) return t('components:state.throttled');
    return t('vacancies:sets.failed');
  })();

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('vacancies:sets.title', { title: vacancy.title })}
      description={t('vacancies:sets.description')}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {t('common:close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-4 text-sm text-ink-900">
        {/*
         * ⚠️ **The offer-not-a-rule sentence, first, because everything below reads differently
         * without it.** It is the client half of ADR 0018 decision 3 and the reason there is no
         * *"remove"* language on the detach: nothing is being taken away from anybody.
         */}
        <p className="text-ink-700">{t('vacancies:sets.offerNotARule')}</p>

        {attached.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : attached.isError ? (
          <p className={cn(type.metadata, 'text-ink-700')}>{t('vacancies:sets.unavailable')}</p>
        ) : rows.length === 0 ? (
          <p className={cn(type.metadata, 'text-ink-700')}>{t('vacancies:sets.none')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-rule border-y border-rule">
            {rows.map((set) => (
              <li key={set.id} className="flex items-baseline justify-between gap-4 py-2">
                <span className={cn('[overflow-wrap:anywhere]', set.is_active || 'text-ink-500')}>
                  {set.name}
                  {/* The state carries its text (§3), never a treatment on its own. */}
                  {!set.is_active && (
                    <span className={cn(type.eyebrow, 'ml-2 whitespace-nowrap')}>
                      {t('vacancies:sets.retired')}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 items-baseline gap-3">
                  <span className={cn(type.metadata, type.numeral)}>
                    {t('vacancies:sets.activeCount', { count: set.active_question_count })}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    isLoading={detach.isPending && detach.variables === set.id}
                    onClick={() => detach.mutate(set.id)}
                  >
                    {t('vacancies:sets.detach')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <Field label={t('vacancies:sets.attachLabel')}>
          {(fieldProps) =>
            catalogue.isPending ? (
              <Skeleton className="h-9 w-full" />
            ) : (
              <Select
                {...fieldProps}
                value={chosen}
                onValueChange={setChosen}
                placeholder={t('vacancies:sets.attachPlaceholder')}
                options={offerable.map((set) => ({ value: String(set.id), label: set.name }))}
              />
            )
          }
        </Field>

        {catalogue.isError && (
          <p className={cn(type.metadata, 'text-ink-700')}>{t('vacancies:sets.catalogueFailed')}</p>
        )}

        {/*
         * ⚠️ **A workspace with more sets than one page holds says so, with both numbers**
         * (`security-auditor` L8, 2026-08-12). `?page_size=` is clamped to
         * `NINJA_MAX_PER_PAGE_SIZE = 100`, so raising it is not available and past 100 this picker
         * would quietly offer a subset of the catalogue an Admin came here to choose from. `count`
         * travels beside the page, so the truncation is knowable rather than guessable — and what is
         * owed is §6.9's range line's rule: how many arrived, out of how many there are.
         */}
        {catalogue.data !== undefined && catalogue.data.count > catalogue.data.items.length && (
          <p className={cn(type.metadata, 'text-ink-700')}>
            {t('vacancies:sets.cataloguePartial', {
              count: catalogue.data.count,
              shown: catalogue.data.items.length,
            })}
          </p>
        )}

        <div>
          <Button
            disabled={chosen === ''}
            isLoading={attach.isPending}
            onClick={() => attach.mutate(Number(chosen))}
          >
            {t('vacancies:sets.attach')}
          </Button>
        </div>

        {failure && (
          <p role="alert" className="text-negative">
            {failure}
          </p>
        )}
      </div>
    </Dialog>
  );
}
